import { isDarkColor } from './colorContrast';
import { PILL_BUTTON_CLASS } from '@/utils/pillButton';

// Se lo sfondo (custom, da admin) è scuro, sovrascrive le custom property
// colore testo/bordo in chiaro — si propaga ai figli per cascata, senza
// dover toccare le classi Tailwind di ogni riga. Stesso meccanismo per la
// sezione superiore e la barra inferiore, ognuna col proprio sfondo.
function darkOverrideStyle(bgColor) {
  if (!bgColor || !isDarkColor(bgColor)) return bgColor ? { backgroundColor: bgColor } : undefined;
  return {
    backgroundColor: bgColor,
    '--color-text': '#FAFAF9',
    '--color-text-muted': 'rgba(250,250,249,.72)',
    '--color-text-subtle': 'rgba(250,250,249,.48)',
    '--color-border': 'rgba(250,250,249,.15)',
  };
}

/**
 * Footer condiviso, layout centrato su due fasce: sezione superiore (logo,
 * tagline, colonne di link, social — tutto centrato) e barra inferiore a
 * tutta larghezza con copyright + link legali sulla stessa riga (separati
 * da "·"), sfondo indipendente dalla sezione superiore. Tutto il contenuto
 * arriva da props — nessun link/testo hardcoded, personalizzabile per progetto.
 *
 * `bgColor`/`bottomBgColor`: se scuro, i token colore testo/bordo vengono
 * sovrascritti in chiaro solo sulla rispettiva sezione (vedi darkOverrideStyle).
 *
 * `linkAs`: componente per i link di colonna/legali — di default un `<a href>`
 * semplice; passa `Link` di react-router-dom per navigazione SPA senza
 * reload tra pagine reali (socialLinks resta sempre `<a>`, sono quasi
 * sempre esterni). Nessun import di router qui, il componente resta agnostico.
 *
 * @param {{
 *   logo: React.ReactNode,
 *   tagline?: string,
 *   addressLine?: React.ReactNode,   // riga extra sotto la tagline (es. indirizzo) — nodo libero, così un progetto può comporla come vuole (testo semplice, o avvolta in decorazioni come le graffe del logo)
 *   columns?: Array<{ title: string, links: Array<{ href: string, label: string }> }>,
 *   bottomLinks?: Array<{ href: string, label: string }>,
 *   socialLinks?: Array<{ href: string, label: string, icon?: React.ReactNode }>,
 *   copyright?: string,
 *   rightSlot?: React.ReactNode,
 *   signature?: React.ReactNode,   // striscia in fondo a tutto (es. firma agenzia "Made in ..."), sotto la barra copyright/legal
 *   bgColor?: string,
 *   bottomBgColor?: string,
 *   signatureBgColor?: string,
 *   legalUppercase?: boolean,      // maiuscolo su copyright + link legali (barra inferiore)
 *   menuUppercase?: boolean,       // maiuscolo sui titoli colonna (sezione superiore)
 *   linkAs?: React.ElementType,
 *   className?: string,
 * }} props
 */
export function SiteFooter({
  logo, tagline, addressLine, columns = [], bottomLinks = [], socialLinks = [],
  copyright, rightSlot, signature,
  bgColor, bottomBgColor, signatureBgColor, legalUppercase = false, menuUppercase = true, linkAs, className = '',
}) {
  const year = new Date().getFullYear();
  const LinkTag = linkAs || 'a';

  return (
    <footer className={['border-t border-theme-border', className].join(' ')}>
      <div className="px-6 pt-8 pb-4" style={darkOverrideStyle(bgColor)}>
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center">
          <div className="text-theme-text">{logo}</div>
          {tagline && <p className="font-heading text-sm font-medium tracking-wide text-theme-text-muted leading-relaxed">{tagline}</p>}
          {addressLine && <p className="font-heading text-sm text-theme-text-muted leading-relaxed">{addressLine}</p>}

          {socialLinks.length > 0 && (
            <div className="flex items-center gap-2.5">
              {socialLinks.map((s) => (
                <a
                  key={s.href} href={s.href} target="_blank" rel="noopener noreferrer"
                  aria-label={s.label} title={s.label}
                  className={PILL_BUTTON_CLASS}
                >
                  {s.icon || s.label}
                </a>
              ))}
            </div>
          )}

          {columns.length > 0 && (
            <div className="mt-2 flex flex-wrap justify-center gap-x-12 gap-y-6">
              {columns.map((col) => (
                <div key={col.title}>
                  <p className={['mb-3 text-xs font-medium text-theme-text-subtle', menuUppercase ? 'uppercase tracking-wider' : ''].join(' ')}>{col.title}</p>
                  <ul className="flex flex-col gap-2">
                    {col.links.map((link) => (
                      <li key={link.href}>
                        <LinkTag
                          {...(linkAs ? { to: link.href } : { href: link.href })}
                          className="text-sm text-theme-text-muted transition-colors hover:text-theme-text"
                        >
                          {link.label}
                        </LinkTag>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4" style={darkOverrideStyle(bottomBgColor)}>
        {/* Copyright e menu legale sono due blocchi separati (non un unico
            flex-wrap) apposta: su schermo stretto vanno a capo ognuno per
            conto proprio — altrimenti un link legale poteva ritrovarsi
            spezzato a metà riga col testo del copyright invece che andare
            a capo pulito come gruppo. */}
        <div
          className={[
            'mx-auto flex max-w-6xl flex-col items-center justify-center gap-y-1.5 text-center text-[10px] text-theme-text-subtle sm:flex-row sm:flex-wrap sm:gap-x-2.5',
            legalUppercase ? 'uppercase tracking-wide' : '',
          ].join(' ')}
        >
          <span>{copyright || `© ${year}`}</span>
          {(bottomLinks.length > 0 || rightSlot) && (
            <span className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5">
              <span aria-hidden="true" className="hidden opacity-50 sm:inline">|</span>
              {bottomLinks.map((link, i) => (
                <span key={link.href} className="flex items-center gap-2.5">
                  {i > 0 && <span aria-hidden="true" className="opacity-50">|</span>}
                  <LinkTag {...(linkAs ? { to: link.href } : { href: link.href })} className="transition-colors hover:text-theme-text">
                    {link.label}
                  </LinkTag>
                </span>
              ))}
              {rightSlot && (
                <span className="flex items-center gap-2.5">
                  {bottomLinks.length > 0 && <span aria-hidden="true" className="opacity-50">|</span>}
                  {rightSlot}
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {signature && (
        <div className="flex justify-center border-t border-theme-border px-6 py-5" style={darkOverrideStyle(signatureBgColor)}>
          {signature}
        </div>
      )}
    </footer>
  );
}
